import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Timer, AlertTriangle, ChevronDown, EyeOff, RotateCcw, Check, X } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useAuth } from '../../../../src/contexts/AuthContext';
import LoadingSpinner from '../../../../src/components/LoadingSpinner';
import SeletorPeriodoRapido from '../../../../src/components/SeletorPeriodoRapido';
import { PeriodoRapido, RangeISO, resolvePeriodoRapido, rangeParaInstantes } from '../../../../src/lib/periodoRapido';
import { computeSlaAgregado, MIN_LEADS_RANKING } from '../../../../src/lib/slaRanking';
import type { RankingLinha, DetalheItem } from '../../../../src/lib/slaRanking';
import { HorarioTrabalho, AusenciaTrabalho, NegociacaoAtribuicao, Profile } from '../../../../types';
import { FUNCOES_COM_RODIZIO } from '../../../../src/lib/departamentos';

const fmtDuracao = (ms: number): string => {
    if (ms <= 0) return '0min';
    const totalMin = Math.round(ms / 60000);
    const dias = Math.floor(totalMin / (24 * 60));
    const horas = Math.floor((totalMin % (24 * 60)) / 60);
    const min = totalMin % 60;
    const partes: string[] = [];
    if (dias > 0) partes.push(`${dias}d`);
    if (horas > 0 || dias > 0) partes.push(`${horas}h`);
    if (dias === 0) partes.push(`${min}min`);
    return partes.join(' ');
};

interface CargaSla {
    itens: DetalheItem[];
    ranking: RankingLinha[];
    mediaBrutoGeral: number | null;
    mediaUtilGeral: number | null;
    totalAtribuicoes: number;
    caiuPorIndisponibilidade: number;
}

const CARGA_VAZIA: CargaSla = { itens: [], ranking: [], mediaBrutoGeral: null, mediaUtilGeral: null, totalAtribuicoes: 0, caiuPorIndisponibilidade: 0 };

/**
 * Busca + agrega o SLA de um período — usada duas vezes: uma pros KPIs/ranking
 * do topo, outra pro detalhamento por negociação, que tem seu PRÓPRIO seletor
 * de período (pedido do usuário: precisava abrir uma negociação de um mês
 * diferente do que os KPIs estavam mostrando, sem perder a visão geral).
 * Módulo, não closure do componente — sem estado, só busca e devolve.
 */
async function carregarSlaDoPeriodo(range: RangeISO): Promise<CargaSla> {
    const { inicio, fim } = rangeParaInstantes(range);

    // Closers e BDRs: quem recebe lead pelo rodízio (antes, o departamento Comercial Interno).
    const { data: perfis } = await supabase.from('profiles').select('id, full_name').in('funcao_comercial', [...FUNCOES_COM_RODIZIO]);
    const perfisPorId = new Map((perfis || []).map((p: Pick<Profile, 'id' | 'full_name'>) => [p.id, p.full_name]));
    const ids = (perfis || []).map(p => p.id);
    if (ids.length === 0) return CARGA_VAZIA;

    const { data: atribuicoes } = await supabase
        .from('negociacao_atribuicoes')
        .select('*, negociacoes(cliente_nome)')
        .in('responsavel_id', ids)
        .gte('atribuido_em', inicio.toISOString())
        .lte('atribuido_em', fim.toISOString());

    const todas = (atribuicoes || []) as Array<NegociacaoAtribuicao & { negociacoes?: { cliente_nome?: string | null } | null }>;
    const totalAtribuicoes = todas.length;
    const caiuPorIndisponibilidade = todas.filter(a => a.caiu_por_indisponibilidade && !a.sla_desconsiderado).length;
    const clienteNomePorNegociacao = new Map(todas.map(a => [a.negociacao_id, a.negociacoes?.cliente_nome]));

    // Só entram na média as que já tiveram atividade — uma ainda aberta não tem
    // uma duração "concluída" pra somar.
    const concluidas = todas.filter(a => a.primeira_atividade_em);
    if (concluidas.length === 0) return { ...CARGA_VAZIA, totalAtribuicoes, caiuPorIndisponibilidade };

    const [{ data: horarios }, { data: ausencias }] = await Promise.all([
        supabase.from('horarios_trabalho').select('*').in('profile_id', ids),
        supabase.from('ausencias_trabalho').select('*').in('profile_id', ids),
    ]);
    const horariosPorPessoa = new Map<string, HorarioTrabalho[]>();
    const ausenciasPorPessoa = new Map<string, AusenciaTrabalho[]>();
    for (const h of (horarios || []) as HorarioTrabalho[]) {
        if (!horariosPorPessoa.has(h.profile_id)) horariosPorPessoa.set(h.profile_id, []);
        horariosPorPessoa.get(h.profile_id)!.push(h);
    }
    for (const a of (ausencias || []) as AusenciaTrabalho[]) {
        if (!ausenciasPorPessoa.has(a.profile_id)) ausenciasPorPessoa.set(a.profile_id, []);
        ausenciasPorPessoa.get(a.profile_id)!.push(a);
    }

    const agregado = computeSlaAgregado(concluidas, horariosPorPessoa, ausenciasPorPessoa, perfisPorId, clienteNomePorNegociacao);
    return {
        itens: agregado.itens,
        ranking: agregado.ranking,
        mediaBrutoGeral: agregado.mediaBrutoGeral,
        mediaUtilGeral: agregado.mediaUtilGeral,
        totalAtribuicoes,
        caiuPorIndisponibilidade,
    };
}

interface KpiCardLocalProps { label: string; value: string; icon: React.ReactNode; tone: string; }
const KpiCardLocal: React.FC<KpiCardLocalProps> = ({ label, value, icon, tone }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-start justify-between">
        <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold">{label}</p>
            <p className={`text-xl font-bold mt-1 ${tone}`}>{value}</p>
        </div>
        <div className={tone}>{icon}</div>
    </div>
);

/**
 * Bloco de SLA de atendimento de Closers e BDRs — auto-contido (busca os
 * próprios dados, não depende do pipeline de filtros do resto do Analytics
 * Comercial, pra não acoplar num componente já muito grande).
 *
 * Bruto = tempo corrido entre atribuir o lead e a primeira atividade humana
 * (nota/status/etapa/transferência — ver negociacao_atribuicoes). Útil = o
 * mesmo intervalo, mas só contando o horário de expediente de cada
 * responsável (escala cadastrada em /config/escalas).
 */
const SlaAtendimentoBlock: React.FC = () => {
    const { profile } = useAuth();
    const podeDesconsiderar = profile?.role === 'adm' || profile?.role === 'gerente';

    const [periodo, setPeriodo] = useState<PeriodoRapido>('mes_atual');
    const [customRange, setCustomRange] = useState<RangeISO | null>(null);
    const [loading, setLoading] = useState(true);
    const [ranking, setRanking] = useState<RankingLinha[]>([]);
    const [mediaBrutoGeral, setMediaBrutoGeral] = useState<number | null>(null);
    const [mediaUtilGeral, setMediaUtilGeral] = useState<number | null>(null);
    const [caiuPorIndisponibilidade, setCaiuPorIndisponibilidade] = useState(0);
    const [totalAtribuicoes, setTotalAtribuicoes] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);

    // Detalhamento por negociação — período PRÓPRIO, independente do período dos
    // KPIs/ranking acima (pedido do usuário: precisa abrir uma negociação de um
    // mês diferente do que está sendo visualizado no geral).
    const [mostrarDetalhamento, setMostrarDetalhamento] = useState(false);
    const [periodoDetalhe, setPeriodoDetalhe] = useState<PeriodoRapido>('mes_atual');
    const [customRangeDetalhe, setCustomRangeDetalhe] = useState<RangeISO | null>(null);
    const [itensDetalhe, setItensDetalhe] = useState<DetalheItem[]>([]);
    const [loadingDetalhe, setLoadingDetalhe] = useState(false);
    const [editandoId, setEditandoId] = useState<string | null>(null);
    const [motivoDraft, setMotivoDraft] = useState('');
    const [salvandoId, setSalvandoId] = useState<string | null>(null);

    const range = useMemo(() => resolvePeriodoRapido(periodo, customRange), [periodo, customRange]);
    const rangeDetalhe = useMemo(() => resolvePeriodoRapido(periodoDetalhe, customRangeDetalhe), [periodoDetalhe, customRangeDetalhe]);

    useEffect(() => {
        // 'personalizado' sem as duas datas escolhidas ainda — não busca nada
        // (o SeletorPeriodoRapido já mantém o calendário aberto pro usuário terminar).
        if (!range) {
            setLoading(false);
            setRanking([]);
            setMediaBrutoGeral(null);
            setMediaUtilGeral(null);
            setCaiuPorIndisponibilidade(0);
            setTotalAtribuicoes(0);
            return;
        }
        let cancelado = false;
        setLoading(true);
        carregarSlaDoPeriodo(range)
            .then(r => {
                if (cancelado) return;
                setRanking(r.ranking);
                setMediaBrutoGeral(r.mediaBrutoGeral);
                setMediaUtilGeral(r.mediaUtilGeral);
                setCaiuPorIndisponibilidade(r.caiuPorIndisponibilidade);
                setTotalAtribuicoes(r.totalAtribuicoes);
            })
            .catch(e => console.error('Erro ao carregar SLA de atendimento:', e))
            .finally(() => { if (!cancelado) setLoading(false); });
        return () => { cancelado = true; };
    }, [range?.inicio, range?.fim, refreshKey]);

    // Só busca quando a seção está aberta — evita um segundo fetch no carregamento inicial.
    useEffect(() => {
        if (!mostrarDetalhamento) return;
        if (!rangeDetalhe) { setItensDetalhe([]); return; }
        let cancelado = false;
        setLoadingDetalhe(true);
        carregarSlaDoPeriodo(rangeDetalhe)
            .then(r => { if (!cancelado) setItensDetalhe(r.itens); })
            .catch(e => console.error('Erro ao carregar detalhamento de SLA:', e))
            .finally(() => { if (!cancelado) setLoadingDetalhe(false); });
        return () => { cancelado = true; };
    }, [mostrarDetalhamento, rangeDetalhe?.inicio, rangeDetalhe?.fim, refreshKey]);

    const piorUtil = useMemo(() => ranking.reduce((max, r) => Math.max(max, r.mediaUtilMs), 1), [ranking]);

    // Piores primeiro — é assim que se acha o outlier ("essa foi 70min, as outras 5-15min").
    const itensOrdenados = useMemo(() => [...itensDetalhe].sort((a, b) => b.utilMs - a.utilMs), [itensDetalhe]);

    const iniciarDesconsiderar = (item: DetalheItem) => { setEditandoId(item.id); setMotivoDraft(''); };
    const cancelarEdicao = () => { setEditandoId(null); setMotivoDraft(''); };

    const confirmarDesconsiderar = async (item: DetalheItem) => {
        setSalvandoId(item.id);
        try {
            const { error } = await supabase
                .from('negociacao_atribuicoes')
                .update({
                    sla_desconsiderado: true,
                    sla_desconsiderado_motivo: motivoDraft.trim() || null,
                    sla_desconsiderado_por: profile?.id || null,
                    sla_desconsiderado_em: new Date().toISOString(),
                })
                .eq('id', item.id);
            if (error) throw error;
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'analytics', acao: 'desconsiderar_sla', origem: 'acao' });
            setEditandoId(null);
            setMotivoDraft('');
            setRefreshKey(k => k + 1);
        } catch (e) {
            console.error('Erro ao desconsiderar SLA:', e);
        } finally {
            setSalvandoId(null);
        }
    };

    const reconsiderar = async (item: DetalheItem) => {
        setSalvandoId(item.id);
        try {
            const { error } = await supabase
                .from('negociacao_atribuicoes')
                .update({ sla_desconsiderado: false, sla_desconsiderado_motivo: null, sla_desconsiderado_por: null, sla_desconsiderado_em: null })
                .eq('id', item.id);
            if (error) throw error;
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'analytics', acao: 'reconsiderar_sla', origem: 'acao' });
            setRefreshKey(k => k + 1);
        } catch (e) {
            console.error('Erro ao reconsiderar SLA:', e);
        } finally {
            setSalvandoId(null);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Timer size={15} className="text-primary" /> SLA de Atendimento — Closers e BDRs
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Tempo entre um lead ser atribuído e a primeira atividade humana nele.</p>
                </div>
                <SeletorPeriodoRapido
                    value={periodo}
                    customRange={customRange}
                    onChange={(p, c) => { setPeriodo(p); setCustomRange(c); }}
                />
            </div>

            {loading ? (
                <div className="py-8"><LoadingSpinner label="Calculando SLA..." size="sm" /></div>
            ) : totalAtribuicoes === 0 ? (
                <p className="text-xs text-gray-400 py-4">Sem leads atribuídos a Closers e BDRs no período.</p>
            ) : mediaBrutoGeral === null ? (
                <p className="text-xs text-gray-400 py-4">Nenhum lead do período teve atividade registrada ainda — sem dado suficiente pro SLA.</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 mb-5">
                        <KpiCardLocal label="Tempo de Resposta Bruto" value={fmtDuracao(mediaBrutoGeral)} icon={<Clock size={20} />} tone="text-secondary" />
                        <KpiCardLocal label="Tempo de Resposta Útil" value={fmtDuracao(mediaUtilGeral || 0)} icon={<Timer size={20} />} tone="text-primary" />
                    </div>

                    {caiuPorIndisponibilidade > 0 && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-xs mb-4">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <span>{caiuPorIndisponibilidade} de {totalAtribuicoes} atribuições caíram fora do horário de expediente de todo mundo (fallback do rodízio) — vale conferir se a escala está bem configurada.</span>
                        </div>
                    )}

                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Ranking individual (menor é melhor)</p>
                    {ranking.length === 0 ? (
                        <p className="text-xs text-gray-400">Ninguém com pelo menos {MIN_LEADS_RANKING} leads concluídos no período ainda.</p>
                    ) : (
                        <div className="space-y-2">
                            {ranking.map((r, i) => (
                                <div key={r.responsavelId} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-400' : 'bg-gray-300 dark:bg-gray-600'}`}>{i + 1}</span>
                                        <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate flex-1">{r.nome}</p>
                                        {r.caiuIndisp > 0 && (
                                            <span
                                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 shrink-0"
                                                title="Leads que caíram fora do horário de expediente de todo mundo (fallback do rodízio)"
                                            >
                                                <AlertTriangle size={10} /> {r.caiuIndisp}
                                            </span>
                                        )}
                                        <span className="text-[11px] font-bold text-primary shrink-0">{fmtDuracao(r.mediaUtilMs)}</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-1.5 text-[11px]">
                                        <div><span className="text-gray-400 block">Leads</span><span className="font-bold text-gray-700 dark:text-gray-200">{r.leads}</span></div>
                                        <div><span className="text-gray-400 block">SLA bruto</span><span className="font-bold text-secondary">{fmtDuracao(r.mediaBrutoMs)}</span></div>
                                        <div><span className="text-gray-400 block">SLA útil</span><span className="font-bold text-primary">{fmtDuracao(r.mediaUtilMs)}</span></div>
                                    </div>
                                    <div className="mt-1.5 h-1 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden" title="SLA útil relativo ao pior colocado — barra menor é melhor">
                                        <div className="h-full bg-primary rounded-full" style={{ width: `${Math.max(4, 100 - (r.mediaUtilMs / piorUtil) * 100)}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Detalhamento por negociação — período próprio, sempre disponível,
                independente do que os KPIs/ranking acima estão mostrando (pedido do
                usuário: precisa abrir uma negociação de outro mês sem perder a
                visão geral que já estava olhando). */}
            <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                    <button
                        onClick={() => setMostrarDetalhamento(v => !v)}
                        className="flex items-center gap-1.5 text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                        <ChevronDown size={13} className={`transition-transform ${mostrarDetalhamento ? 'rotate-180' : ''}`} />
                        Ver detalhamento por negociação{itensDetalhe.length > 0 ? ` (${itensDetalhe.length})` : ''}
                    </button>
                    {mostrarDetalhamento && (
                        <SeletorPeriodoRapido
                            value={periodoDetalhe}
                            customRange={customRangeDetalhe}
                            onChange={(p, c) => { setPeriodoDetalhe(p); setCustomRangeDetalhe(c); }}
                        />
                    )}
                </div>

                {mostrarDetalhamento && (
                    loadingDetalhe ? (
                        <div className="py-6"><LoadingSpinner label="Carregando detalhamento..." size="sm" /></div>
                    ) : itensDetalhe.length === 0 ? (
                        <p className="text-xs text-gray-400 py-4">Nenhuma negociação concluída nesse período.</p>
                    ) : (
                        <div className="mt-3 space-y-1.5 max-h-96 overflow-y-auto custom-scrollbar pr-1">
                            {itensOrdenados.map(item => {
                                        const editando = editandoId === item.id;
                                        const salvando = salvandoId === item.id;
                                        return (
                                            <div
                                                key={item.id}
                                                className={`px-3 py-2 rounded-lg border text-xs ${item.slaDesconsiderado
                                                    ? 'bg-gray-50 dark:bg-gray-700/30 border-gray-100 dark:border-gray-700 opacity-60'
                                                    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-100 dark:border-gray-700'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <p className="font-bold text-gray-800 dark:text-gray-100 truncate">
                                                            {item.clienteNome || `Negociação #${item.negociacaoId}`}
                                                            <span className="font-normal text-gray-400"> · {item.nome}</span>
                                                        </p>
                                                        {item.slaDesconsiderado && (
                                                            <p className="text-[10.5px] text-gray-400 italic truncate">
                                                                Desconsiderado{item.slaDesconsideradoMotivo ? `: ${item.slaDesconsideradoMotivo}` : ''}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className={`font-bold tabular-nums ${item.slaDesconsiderado ? 'text-gray-400 line-through' : 'text-primary'}`}>
                                                            {fmtDuracao(item.utilMs)}
                                                        </span>
                                                        {podeDesconsiderar && !editando && (
                                                            item.slaDesconsiderado ? (
                                                                <button
                                                                    onClick={() => reconsiderar(item)}
                                                                    disabled={salvando}
                                                                    title="Voltar a considerar esta negociação no cálculo"
                                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 disabled:opacity-50"
                                                                >
                                                                    <RotateCcw size={13} />
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => iniciarDesconsiderar(item)}
                                                                    title="Desconsiderar esta negociação do cálculo de SLA"
                                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                                                >
                                                                    <EyeOff size={13} />
                                                                </button>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                                {editando && (
                                                    <div className="flex items-center gap-1.5 mt-2">
                                                        <input
                                                            autoFocus
                                                            value={motivoDraft}
                                                            onChange={e => setMotivoDraft(e.target.value)}
                                                            placeholder="Motivo (ex.: Estávamos testando)"
                                                            className="flex-1 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 text-[11px] bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                                                        />
                                                        <button onClick={cancelarEdicao} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={13} /></button>
                                                        <button
                                                            onClick={() => confirmarDesconsiderar(item)}
                                                            disabled={salvando}
                                                            className="p-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                                                        >
                                                            <Check size={13} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default SlaAtendimentoBlock;
